/**
 * VÉRAULT SVG Icon System
 * 
 * Brand-level "VÉRAULT emojis" - minimal, calm, soft, editorial luxury
 * Consistent stroke weight, rounded elegant shapes
 * 
 * Approved colors ONLY:
 * - Bone: #F5F1EB
 * - Charcoal: #1A1A1A
 * - Stone: #8C8A86
 * - Micro-gold: #C8A85A (rare, intentional use only)
 */

import React from 'react';

// Color constants
export const VERAULT_COLORS = {
  bone: '#F5F1EB',
  charcoal: '#1A1A1A',
  stone: '#8C8A86',
  gold: '#C8A85A'
};

// Base icon wrapper with consistent sizing
const IconWrapper = ({ children, size = 20, className = '' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {children}
  </svg>
);

// ==================== LIFECYCLE ICONS ====================

// Planning Initiated - Sparkle/Star
export const IconSparkle = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <circle cx="19" cy="5" r="1.5" stroke={color} strokeWidth="1.5" />
    <circle cx="5" cy="19" r="1.5" stroke={color} strokeWidth="1.5" />
  </IconWrapper>
);

// Ready for Review - Eye
export const IconEye = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.5" />
  </IconWrapper>
);

// Approved - Check
export const IconCheck = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M20 6L9 17L4 12" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Booking in Progress - Clock
export const IconClock = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" />
    <path 
      d="M12 7V12L15 15" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Booked / In Travel - Plane
export const IconPlane = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M21 16V8C21 7 20 6 19 6H14L10 2H8L10 6H5L3 4H1L3 8L1 12H3L5 10H10L8 14H10L14 10H19C20 10 21 9 21 8" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M2 16L4 18L6 16" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M18 20L20 22L22 20" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Simplified Plane (cleaner)
export const IconPlaneSolid = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Globe / World - In Travel
export const IconGlobe = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" />
    <path 
      d="M3 12H21" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
    <path 
      d="M12 3C14.5 5.5 15.5 8.5 15.5 12C15.5 15.5 14.5 18.5 12 21" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
    <path 
      d="M12 3C9.5 5.5 8.5 8.5 8.5 12C8.5 15.5 9.5 18.5 12 21" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
  </IconWrapper>
);

// Completed - Heart (warm, emotional - brand guidelines)
export const IconHeart = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.388 3.14052 17.6726 2.99817 16.95 2.99817C16.2274 2.99817 15.512 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.1917 3.57831 3.16 4.61C2.1283 5.6417 1.5487 7.04097 1.5487 8.5C1.5487 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93801 22.4518 9.22261 22.4518 8.5C22.4518 7.77739 22.3095 7.06199 22.0329 6.39462C21.7563 5.72724 21.351 5.12083 20.84 4.61Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// ==================== COMMAND CENTER ICONS ====================

// Travelers / Users
export const IconTravelers = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="9" cy="7" r="4" stroke={color} strokeWidth="1.5" />
    <path 
      d="M2 21V19C2 16.7909 3.79086 15 6 15H12C14.2091 15 16 16.7909 16 19V21" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
    <circle cx="17" cy="8" r="3" stroke={color} strokeWidth="1.5" />
    <path 
      d="M18 15H19C20.6569 15 22 16.3431 22 18V21" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
  </IconWrapper>
);

// Calendar / Dates
export const IconCalendar = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <rect x="3" y="4" width="18" height="18" rx="2" stroke={color} strokeWidth="1.5" />
    <path d="M16 2V6" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 2V6" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M3 10H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="15" r="1" fill={color} />
    <circle cx="12" cy="15" r="1" fill={color} />
    <circle cx="16" cy="15" r="1" fill={color} />
  </IconWrapper>
);

// Map Pin / Destination
export const IconMapPin = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M12 21C12 21 19 14.5 19 9C19 5.13401 15.866 2 12 2C8.13401 2 5 5.13401 5 9C5 14.5 12 21 12 21Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <circle cx="12" cy="9" r="3" stroke={color} strokeWidth="1.5" />
  </IconWrapper>
);

// Document / Itinerary
export const IconDocument = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 13H16" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 17H12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </IconWrapper>
);

// Dollar / Estimate
export const IconDollar = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" />
    <path 
      d="M12 6V18M15 9C15 9 14.5 7.5 12 7.5C9.5 7.5 9 9 9 9.75C9 10.5 9.5 11.5 12 12C14.5 12.5 15 13.5 15 14.25C15 15 14.5 16.5 12 16.5C9.5 16.5 9 15 9 15" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Shield / Agreement
export const IconShield = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M12 2L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 2Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path d="M9 12L11 14L15 10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// Cloud / Weather
export const IconWeather = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M18 10H17.74C17.3659 8.5506 16.4802 7.28173 15.2462 6.42066C14.0122 5.5596 12.5125 5.16118 11.012 5.29664C9.5115 5.4321 8.10773 6.09278 7.04473 7.16061C5.98173 8.22845 5.32771 9.63518 5.2 11.136C4.0517 11.3565 3.0324 11.9995 2.34247 12.9384C1.65254 13.8773 1.34237 15.0408 1.4718 16.1993C1.60123 17.3578 2.16076 18.4262 3.03914 19.1924C3.91752 19.9586 5.05159 20.3679 6.216 20.346H18C19.1935 20.346 20.3381 19.8717 21.182 19.0279C22.0259 18.184 22.5 17.0394 22.5 15.846C22.5 14.6525 22.0259 13.5079 21.182 12.6641C20.3381 11.8202 19.1935 11.346 18 11.346V10Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Arrow / External Link
export const IconExternal = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M15 3H21V9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 14L21 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </IconWrapper>
);

// Alert / Warning
export const IconAlert = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" />
    <path d="M12 8V12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="16" r="0.5" fill={color} stroke={color} strokeWidth="1" />
  </IconWrapper>
);

// Check Circle (Complete)
export const IconCheckCircle = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" />
    <path d="M9 12L11 14L15 10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// ==================== NAVIGATION ICONS ====================

// Arrow Left / Back
export const IconArrowLeft = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// Arrow Right
export const IconArrowRight = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// Plus
export const IconPlus = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M12 5V19M5 12H19" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// X / Close
export const IconClose = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </IconWrapper>
);

// Settings / Gear
export const IconSettings = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.5" />
    <path 
      d="M12 1V4M12 20V23M4.22 4.22L6.34 6.34M17.66 17.66L19.78 19.78M1 12H4M20 12H23M4.22 19.78L6.34 17.66M17.66 6.34L19.78 4.22" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round"
    />
  </IconWrapper>
);

// Hotel / Lodging
export const IconHotel = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path d="M3 21H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M5 21V7L12 3L19 7V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="9" y="13" width="6" height="8" stroke={color} strokeWidth="1.5" />
    <path d="M9 9H10.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M13.5 9H15" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </IconWrapper>
);

// Send / Paper Plane
export const IconSend = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Clipboard - Details Gathering stage
export const IconClipboard = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M16 4H18C19.1046 4 20 4.89543 20 6V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V6C4 4.89543 4.89543 4 6 4H8" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <rect x="8" y="2" width="8" height="4" rx="1" stroke={color} strokeWidth="1.5" />
    <path d="M8 10H16" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 14H16" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 18H12" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </IconWrapper>
);

// Pencil / Edit - Design in Progress stage
export const IconPencil = ({ size = 20, color = VERAULT_COLORS.stone, className = '' }) => (
  <IconWrapper size={size} className={className}>
    <path 
      d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </IconWrapper>
);

// Export all icons as a mapping for easy lookup
export const VERAULT_ICONS = {
  sparkle: IconSparkle,
  eye: IconEye,
  check: IconCheck,
  clock: IconClock,
  plane: IconPlaneSolid,
  globe: IconGlobe,
  heart: IconHeart,
  travelers: IconTravelers,
  calendar: IconCalendar,
  mapPin: IconMapPin,
  document: IconDocument,
  dollar: IconDollar,
  shield: IconShield,
  weather: IconWeather,
  external: IconExternal,
  alert: IconAlert,
  checkCircle: IconCheckCircle,
  arrowLeft: IconArrowLeft,
  arrowRight: IconArrowRight,
  plus: IconPlus,
  close: IconClose,
  settings: IconSettings,
  hotel: IconHotel,
  send: IconSend,
  clipboard: IconClipboard,
  pencil: IconPencil
};

// Helper to get icon by name
export const getVeraultIcon = (name, props = {}) => {
  const Icon = VERAULT_ICONS[name];
  if (!Icon) return null;
  return <Icon {...props} />;
};

export default VERAULT_ICONS;
